import { cn } from '@folio/lib';
import { getEnvironment, isKorintUser } from '@folio/utils';
import { useQuery } from '@tanstack/react-query';
import { legacyPermissionsControllerAtMeQueryOptions } from 'korint-sdk';
import { Group } from '@folio/components/ui/flex';

const ENV_STYLES = {
  LOCAL: 'bg-green-500',
  STAGING: 'bg-blue-500',
  REVIEW: 'bg-orange-500',
  PREVIEW: 'bg-amber-500',
  PRODUCTION: 'bg-red-500',
} as const;

function getLocalLabel(): string {
  const branch = (import.meta.env.VITE_GIT_BRANCH as string | undefined) ?? '';
  try {
    const url = new URL(import.meta.env.VITE_KORINT_API_URL ?? '');
    return branch ? `LOCAL :${url.port} (${branch})` : `LOCAL :${url.port}`;
  } catch {
    return branch ? `LOCAL (${branch})` : 'LOCAL';
  }
}

const ENV_LABELS = {
  LOCAL: getLocalLabel(),
  STAGING: 'STAGING',
  REVIEW: 'REVIEW',
  PREVIEW: 'PREVIEW',
  PRODUCTION: 'PRODUCTION',
} as const;

const MARQUEE_ITEMS = 20;

interface EnvironmentBannerProps {
  /** Durée du défilement en secondes. Plus bas = plus rapide. Défaut : 12 */
  speed?: number;
}

export function EnvironmentBanner({ speed }: Readonly<EnvironmentBannerProps>) {
  const env = getEnvironment();
  const effectiveSpeed = speed ?? (env === 'LOCAL' ? 120 : 50);

  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
    enabled: env === 'PRODUCTION',
  });

  if (import.meta.env.VITEST) return null;
  if (import.meta.env.VITE_TENANT_ID === 'DEMO') return null;
  if (env === 'PRODUCTION' && !isKorintUser(user?.email)) return null;

  return (
    <div
      className={cn(
        'w-full overflow-hidden text-white text-[11px] font-bold tracking-widest uppercase shadow-md select-none py-1 shrink-0',
        ENV_STYLES[env],
      )}
    >
      <style>{`
        @keyframes env-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .env-marquee {
          animation: env-marquee ${effectiveSpeed}s linear infinite;
        }
      `}</style>
      <Group align="stretch" gap={0} className="env-marquee whitespace-nowrap w-max">
        {Array.from({ length: MARQUEE_ITEMS }).map((_, i) => (
          <span key={i} className="px-6">
            {ENV_LABELS[env]}
          </span>
        ))}
        {Array.from({ length: MARQUEE_ITEMS }).map((_, i) => (
          <span key={`dup-${i}`} className="px-6">
            {ENV_LABELS[env]}
          </span>
        ))}
      </Group>
    </div>
  );
}
